import {observer} from "mobx-react";
import {useContext} from "react";
import {
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import {School, Person, Visibility} from "@mui/icons-material";
import {Frame} from "../common/Frame";
import {RecordingStoreContext} from "../../stores/RecordingStore";
import {RecordingsStore} from "../../stores/RecordingsStore";
import {AppContext} from "../../stores/AppStore";
import {DateUtils} from "../../utils/DateUtils";
import {recordings} from "../../proto/compiled";
import {MobxUtils} from "../../utils/MobxUtils";
import {D} from "../../Constants";
import {useNavigate} from "react-router";
import {AppRoutes} from "../../AppRoutes";

interface RecordingMetaPanelProps {
  compact?: boolean;
  dialog?: boolean;
  onClose?: () => void;
}

export const RecordingMetaPanel = observer(({compact = false, dialog = false, onClose}: RecordingMetaPanelProps) => {
  const store = useContext(RecordingStoreContext);
  const app = useContext(AppContext);
  const theme = useTheme();
  const navigate = useNavigate();

  if (!store || !store.recording?.recording) {
    return compact ? null : (
      <Frame grow center padding={3}>
        <Typography variant="body1">
          Loading session information...
        </Typography>
      </Frame>
    );
  }

  const rec = store.recording.recording;
  const isEditing = store.editingRecording;
  const isSaving = store.savingRecording;

  const getParticipantIcon = (role?: recordings.RecordingParticipantRole | null) => {
    switch (role) {
    case recordings.RecordingParticipantRole.rpr_instructor:
      return <School fontSize="small"/>;
    case recordings.RecordingParticipantRole.rpr_student:
      return <Person fontSize="small"/>;
    case recordings.RecordingParticipantRole.rpr_supervisor:
      return <Visibility fontSize="small"/>;
    default:
      return undefined;
    }
  };

  const handleSave = async () => {
    await store.saveRecording();
  };

  const handleCancel = () => {
    store.editingRecording = false;
    store.load(); // Reload to reset changes
  };

  const handleDelete = () => {
    if (!app || !rec.id) return;
    app.confirmationDialogText = `Are you sure you want to delete "${rec.title}"?`;
    app.confirmationDialogAction = async () => {
      await store.deleteRecording();
      RecordingsStore.recordingDeleted.next(rec.id!);
      navigate(AppRoutes.mySessions);
    };
  };

  const addParticipant = () => {
    if (!rec.participants) {
      rec.participants = [];
    }
    rec.participants.push(MobxUtils.makeObservable({
      id: `temp-${Date.now()}`,
      name: "",
      role: recordings.RecordingParticipantRole.rpr_other,
    }));
  };

  const removeParticipant = (index: number) => {
    if (rec.participants) {
      rec.participants.splice(index, 1);
    }
  };

  if (compact) {
    return (
      <Frame row gap={2} padding={2} alignItems={"flex-start"}>
        {/* Compact thumbnail */}
        {rec.thumbnailUrl && (
          <Frame
            radius={1.5}
            clip
            style={{
              border: `1px solid ${theme.palette.divider}`,
              width: '200px',
              flexShrink: 0,
            }}
          >
            <img
              src={rec.thumbnailUrl}
              style={{width: "100%", aspectRatio: 16 / 9, display: "block", objectFit: "cover"}}
              alt="Recording thumbnail"
            />
          </Frame>
        )}

        {/* Compact info */}
        <Frame direction="column" gap={1.5} grow>
          <Typography variant="h6" fontWeight={600}>
            {rec.title || "Untitled Session"}
          </Typography>

          {rec.description && (
            <Typography
              variant="body2"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.6,
              }}
            >
              {rec.description}
            </Typography>
          )}

          <Frame row gap={2} wrap>
            <Typography variant="body2">
              {DateUtils.toString(rec.dateCreated)}
            </Typography>
            {rec.duration && (
              <Typography variant="body2">
                {rec.duration}
              </Typography>
            )}
          </Frame>

          {!!rec.participants?.length && (
            <Frame row wrap gap={0.5}>
              {rec.participants.slice(0, 3).map((participant) => (
                <Chip
                  key={participant.id ?? participant.name}
                  size="small"
                  icon={getParticipantIcon(participant.role)}
                  label={participant.name}
                />
              ))}
              {rec.participants.length > 3 && (
                <Chip size="small" label={`+${rec.participants.length - 3} more`} />
              )}
            </Frame>
          )}
        </Frame>
      </Frame>
    );
  }

  return (
    <Frame direction="column" grow style={{display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
      {/* Header - only show in dialog mode */}
      {dialog && (
        <Frame
          row
          alignItems="center"
          justifyContent="space-between"
          padding={3}
          style={{flexShrink: 0, paddingBottom: '16px'}}
        >
          <Typography variant="h4">
            Session Information
          </Typography>
          <Frame row gap={1} alignItems="center">
            {!isEditing ? (
              <IconButton
                size="small"
                onClick={() => store.editingRecording = true}
                aria-label="Edit session info"
              >
                <EditIcon fontSize="small"/>
              </IconButton>
            ) : (
              <>
                {rec.isOwner && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon/>}
                    onClick={handleDelete}
                    disabled={isSaving}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CancelIcon/>}
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon/>}
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  Save
                </Button>
              </>
            )}
            {onClose && (
              <IconButton
                onClick={() => {
                  if (isEditing) {
                    handleCancel();
                  }
                  onClose();
                }}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          </Frame>
        </Frame>
      )}

      {/* Scrollable content */}
      <Frame direction="column" grow scroll>
        <Frame direction="column" contentWidth paddingH={3} gap={2}>

          {/* Thumbnail */}
          {rec.thumbnailUrl && (
            <Frame
              radius={1.5}
              clip
              width={'50%'}
              style={{
                border: `1px solid ${theme.palette.divider}`,
                alignItems: 'center',
              }}
            >
              <img
                src={rec.thumbnailUrl}
                style={{width: "100%", aspectRatio: 16 / 9, display: "block", objectFit: "cover"}}
                alt="Recording thumbnail"
              />
            </Frame>
          )}

          {/* Title */}
          <Frame direction="column" gap={1}>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={rec.title ?? ""}
                onChange={(e) => {
                  rec.title = e.target.value;
                }}
                placeholder="Enter session title"
              />
            ) : (
              <Typography variant="h6" fontWeight={600}>
                {rec.title || "Untitled Session"}
              </Typography>
            )}
          </Frame>

          {/* Description */}
          <Frame direction="column" gap={1}>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                maxRows={8}
                value={rec.description ?? ""}
                onChange={(e) => {
                  rec.description = e.target.value;
                }}
                placeholder="Enter session description"
              />
            ) : (
              <Typography variant="body2" style={{whiteSpace: "pre-wrap", lineHeight: 1.6, marginTop: D.unit(-2)}}>
                {rec.description || "No description"}
              </Typography>
            )}
          </Frame>

          {/* Participants */}
          <Frame direction="column" gap={1.5}>
            <Frame row alignItems="center" justifyContent="space-between">
              <Typography variant="caption" fontWeight={600} style={{letterSpacing: '0.5px'}}>
                PARTICIPANTS
              </Typography>
              {isEditing && (
                <IconButton size="small" onClick={addParticipant}>
                  <AddIcon fontSize="small"/>
                </IconButton>
              )}
            </Frame>

            {isEditing ? (
              <Frame direction="column" gap={1.5}>
                {rec.participants?.map((participant, index) => (
                  <Frame key={index} row gap={1} alignItems="center">
                    <TextField
                      size="small"
                      value={participant.name ?? ""}
                      onChange={(e) => {
                        participant.name = e.target.value;
                      }}
                      placeholder="Name"
                      style={{flex: 2}}
                    />
                    <FormControl size="small" style={{flex: 1}}>
                      <Select
                        value={participant.role ?? recordings.RecordingParticipantRole.rpr_other}
                        onChange={(e) => {
                          participant.role = e.target.value as recordings.RecordingParticipantRole;
                        }}
                      >
                        <MenuItem value={recordings.RecordingParticipantRole.rpr_other}>Other</MenuItem>
                        <MenuItem value={recordings.RecordingParticipantRole.rpr_instructor}>Instructor</MenuItem>
                        <MenuItem value={recordings.RecordingParticipantRole.rpr_student}>Student</MenuItem>
                        <MenuItem value={recordings.RecordingParticipantRole.rpr_supervisor}>Supervisor</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={() => removeParticipant(index)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small"/>
                    </IconButton>
                  </Frame>
                ))}
                {(!rec.participants || rec.participants.length === 0) && (
                  <Typography variant="body2" fontStyle="italic">
                    No participants added
                  </Typography>
                )}
              </Frame>
            ) : (
              <Frame row wrap gap={1} marginTop={-1}>
                {rec.participants?.map((participant) => (
                  <Chip
                    key={participant.id ?? participant.name}
                    size="medium"
                    icon={getParticipantIcon(participant.role)}
                    label={participant.name}
                  />
                ))}
                {(!rec.participants || rec.participants.length === 0) && (
                  <Typography variant="body2" fontStyle="italic">
                    No participants
                  </Typography>
                )}
              </Frame>
            )}
          </Frame>
          {/* Date Created & Duration */}
          <Frame row gap={4} wrap>
            <Frame direction="column" style={{flex: 1, minWidth: '200px'}}>
              <Typography variant="caption" fontWeight={600} style={{letterSpacing: '0.5px'}}>
                DATE CREATED
              </Typography>
              <Typography variant="body2">
                {DateUtils.toString(rec.dateCreated)}
              </Typography>
            </Frame>

            {rec.duration && (
              <Frame direction="column" style={{flex: 1, minWidth: '200px'}}>
                <Typography variant="caption" fontWeight={600} style={{letterSpacing: '0.5px'}}>
                  DURATION
                </Typography>
                <Typography variant="body2">
                  {rec.duration}
                </Typography>
              </Frame>
            )}
          </Frame>

        </Frame>
      </Frame>
    </Frame>
  );
});
